import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { 
  ArrowLeft, Bell, CheckCheck, Trash2, Info, 
  TrendingUp, MessageCircle, AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Notifications = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readIds, setReadIds] = useState(new Set());

  useEffect(() => {
    fetchNotifications();
    fetchReadStatus();
  }, [token]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReadStatus = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.read_notifications) {
        setReadIds(new Set(res.data.read_notifications));
      }
    } catch (error) {
      console.error("Error fetching read status:", error);
    }
  };

  const markAsRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReadIds(prev => new Set([...prev, notifId]));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allIds = notifications.map(n => n.id);
      setReadIds(new Set(allIds));
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "только что";
    if (diffMins < 60) return `${diffMins} мин. назад`;
    if (diffHours < 24) return `${diffHours} ч. назад`;
    if (diffDays < 7) return `${diffDays} дн. назад`;
    
    return date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Moscow"
    });
  };

  const getIcon = (type) => {
    switch (type) {
      case "system":
        return <TrendingUp size={20} className="text-[#34D399]" />;
      case "admin":
        return <MessageCircle size={20} className="text-[#EDEDED]" />;
      default:
        return <Info size={20} className="text-blue-500" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "system":
        return "Система";
      case "admin":
        return "Администрация";
      default:
        return "Уведомление";
    }
  };

  const unreadCount = notifications.filter(n => !readIds.has(n.id)).length;

  if (loading) {
    return (
      <div className="min-h-screen bybit-grid-subtle flex items-center justify-center">
        <div className="text-[#EDEDED]">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bybit-grid-subtle">
      {/* Header */}
      <header className="border-b border-[#1F1F1F] bg-[#000000] sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/dashboard")}
                className="text-[#A1A1AA] hover:text-white transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              <div className="flex items-center gap-2">
                <Bell size={24} className="text-[#EDEDED]" />
                <h1 className="font-bold text-xl text-white">Уведомления</h1>
                {unreadCount > 0 && (
                  <span className="bg-[#F87171] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#EDEDED] transition-colors text-sm"
              >
                <CheckCheck size={18} />
                <span className="hidden sm:inline">Прочитать все</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {notifications.length === 0 ? (
          <div className="text-center py-16">
            <Bell size={48} className="mx-auto text-[#1F1F1F] mb-4" />
            <p className="text-[#A1A1AA]">Нет уведомлений</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notif) => {
              const isRead = readIds.has(notif.id);
              
              return (
                <div
                  key={notif.id}
                  onClick={() => !isRead && markAsRead(notif.id)}
                  className={`
                    bg-[#0A0A0A] rounded-lg border p-4 transition-all cursor-pointer
                    ${isRead 
                      ? 'border-[#1F1F1F] opacity-70' 
                      : 'border-white/30 hover:border-[#2E2E2E]/50'
                    }
                  `}
                >
                  <div className="flex gap-4">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                      ${notif.type === 'system' ? 'bg-[#34D399]/20' : 'bg-white/20'}
                    `}>
                      {getIcon(notif.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          {!isRead && (
                            <span className="w-2 h-2 bg-white rounded-full"></span>
                          )}
                        </div>
                        <span className="text-[#A1A1AA] text-xs whitespace-nowrap">
                          {formatDate(notif.created_at)}
                        </span>
                      </div>
                      <h3 className="font-semibold text-white mt-2">{notif.title}</h3>
                      <p className="text-[#A1A1AA] text-sm mt-1">{notif.message}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Notifications;
